import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaSpinner, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import api from '../api';
import ZerodhaBrokerErrorAlert from '../components/ZerodhaBrokerErrorAlert';
import { getBrokerErrorMessage, isZerodhaApiKeyRejectedError } from '../utils/zerodhaBrokerErrors';

const ZerodhaCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('');
  const [lastError, setLastError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      const requestToken = searchParams.get('request_token');
      const status = searchParams.get('status');
      const oauthState = searchParams.get('state');
      const flowHint = sessionStorage.getItem('zerodha_auth_flow');
      const isSharedAdminFlow =
        oauthState === 'shared_zerodha_admin' || flowHint === 'shared_zerodha_admin';

      // Check if user denied authorization
      if (status === 'cancelled' || !requestToken) {
        setStatus('error');
        setMessage('Authorization cancelled. Please try again.');
        
        // Notify parent window
        if (window.opener) {
          window.opener.postMessage({
            type: 'ZERODHA_AUTH_ERROR',
            error: 'Authorization cancelled'
          }, window.location.origin);
        }
        
        // Close window after delay
        setTimeout(() => {
          sessionStorage.removeItem('zerodha_auth_flow');
          if (window.opener) {
            window.close();
          } else {
            navigate(isSharedAdminFlow ? '/admin/shared-zerodha' : '/broker-integrations');
          }
        }, 2000);
        return;
      }

      try {
        if (isSharedAdminFlow) {
          // Shared admin flow: hand over directly to backend callback endpoint
          // so token rotation + DB save happen server-side without frontend auth dependency.
          const backendBase = (api?.defaults?.baseURL || window.location.origin).replace(/\/$/, '');
          const callbackBaseUrl = new URL('/api/v1/admin/settings/shared-zerodha/callback', `${backendBase}/`).toString();
          const qs = new URLSearchParams({
            request_token: requestToken,
            ...(status ? { status } : {}),
          }).toString();
          sessionStorage.removeItem('zerodha_auth_flow');
          window.location.href = `${callbackBaseUrl}?${qs}`;
          return;
        } else {
          // User broker flow: connect current user's broker credentials.
          const response = await api.post('/api/v1/brokers/zerodha/connect', {
            request_token: requestToken
          });

          // Clear stored credentials (for backward compatibility)
          sessionStorage.removeItem('zerodha_api_key');
          sessionStorage.removeItem('zerodha_api_secret');

          setStatus('success');
          setMessage('Broker connected successfully!');

          // Notify parent window of success
          if (window.opener) {
            window.opener.postMessage({
              type: 'ZERODHA_AUTH_SUCCESS',
              data: response.data
            }, window.location.origin);
          }

          // Close window after brief delay
          setTimeout(() => {
            sessionStorage.removeItem('zerodha_auth_flow');
            if (window.opener) {
              window.close();
            } else {
              navigate('/broker-integrations');
            }
          }, 2000);
          return;
        }

      } catch (error) {
        console.error('Connection error:', error);

        const errorMessage = getBrokerErrorMessage(
          error,
          'Failed to connect broker. Please try again.'
        );
        const errorCode = isZerodhaApiKeyRejectedError(error)
          ? 'zerodha_api_key_rejected'
          : 'connect_failed';

        setLastError(error);
        setStatus('error');
        setMessage(errorMessage);

        if (window.opener) {
          window.opener.postMessage({
            type: 'ZERODHA_AUTH_ERROR',
            error: errorMessage,
            code: errorCode,
          }, window.location.origin);
        }

        // Redirect after delay
        setTimeout(() => {
          sessionStorage.removeItem('zerodha_auth_flow');
          if (window.opener) {
            window.close();
          } else {
            navigate(isSharedAdminFlow ? '/admin/shared-zerodha' : '/broker-integrations');
          }
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <FaSpinner className="animate-spin text-6xl text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Connecting Your Account...
            </h2>
            <p className="text-gray-600">
              Please wait while we securely connect your Zerodha account.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaCheckCircle className="text-4xl text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Success!
            </h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">
              This window will close automatically...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaExclamationTriangle className="text-4xl text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Connection Failed
            </h2>
            {isZerodhaApiKeyRejectedError(lastError) ? (
              <ZerodhaBrokerErrorAlert message={message} err={lastError} className="mb-4 text-left" />
            ) : (
              <p className="text-gray-600 mb-4">{message}</p>
            )}
            <p className="text-sm text-gray-500">
              {window.opener ? 'Closing window...' : 'Redirecting...'}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ZerodhaCallbackPage;

